<!--

    CabinetForm.vue
    ---------------
    This component is form used to add or modify a medicine in the user's cabinet.

    Props:

    - medication: The medication being added or modified.
    - mode: The mode of operation, i.e., "create" or "update".

    Events:

    - error(error): Called when there is an error encountered while saving data on the server.
    - save(mode): Called when the medication is saved successfully.
    - remove(medication): Called when a medication is removed.
    - labels-save: Called when the labels are saved successfully after modification.

-->
<template>
    <div class="cabinet-form">
        <!-- label edit form -->
        <b-modal :active.sync="isLabelEditFormOpen" has-modal-card id="label-edit-form">
            <div class="modal-card">
                <header class="modal-card-head">{{ editLabelsText }}</header>

                <section class="modal-card-body">
                    <div class="columns is-mobile" v-if="labels" v-for="label in tempLabels">
                        <div class="column is-2 label-icon">
                            <i v-bind:class="[label.IconClass, label.ColorClass]" class="fa-lg"></i>
                        </div>
                        <div class="column label-text">
                            <input type="text" maxlength="50" class="input" v-model="label.Text" @change="labelTextChange(label)" :placeholder="labelPlaceholderText" />
                        </div>
                    </div>
                </section>

                <footer class="modal-card-foot">
                    <button class="button is-primary" @click="labelEditSaveClick">{{ saveText }}</button>
                </footer>
            </div>
        </b-modal>

        <!-- label toggle form -->
        <b-modal :active.sync="isLabelFormOpen" has-modal-card id="label-form">
            <div class="modal-card">
                <header class="modal-card-head">{{ toggleLabelsText }}</header>

                <section class="modal-card-body">
                    <div class="columns is-mobile" v-if="labels" v-for="label in labels">
                        <div class="is-2 column label-icon">
                            <i v-bind:class="[label.IconClass, label.ColorClass]" class="fa-lg"></i>
                        </div>
                        <div class="column label-text">
                            <span v-show="label.Text">{{ label.Text }}</span>
                            <span v-show="!label.Text" class="unset">{{ setLabelsText }}</span>
                        </div>
                        <div class="is-2 column clickable">
                            <b-checkbox :native-value="label.LabelId" v-model="checkedLabels" />
                        </div>
                    </div>
                </section>

                <footer class="modal-card-foot">
                    <button class="button is-primary" type="button" @click="toggleLabelForm">{{ okText }}</button>
                </footer>
            </div>
        </b-modal>

        <div class="content" v-bind:class="buttonsClass" v-if="currentMedication">
            <div class="columns">
                <div class="column">
                    <h5 class="ln">{{currentMedication.Ln}}</h5>
                </div>
            </div>

            <form id="cabinet-form">
                <div class="columns">
                    <div class="column">
                        <fieldset class="fieldset">
                            <legend>{{ requiredText }}</legend>
                            <div class="columns">
                                <div class="column">
                                    <div class="control has-icons-left">
                                        <b-field :label="fillDateText" :type="{ 'is-danger': errors.has('fill date') }" :message="errors.first('fill date')">
                                            <b-datepicker v-model="currentMedication.FillDate"
                                                          :disabled="isFromRxHistory"
                                                          :formatter="dateFormatter"
                                                          :placeholder="requiredText"
                                                          :month-names="monthText"
                                                          position="is-bottom-left"
                                                          icon="calendar"
                                                          icon-pack="fa"
                                                          v-validate="'required'"
                                                          name="fill date"
                                                          :key="currentMedication.Id + 'FillDate'"
                                                          editable>
                                            </b-datepicker>
                                        </b-field>
                                    </div>
                                </div>
                            </div>
                        </fieldset>
                    </div>
                </div>

                <div class="columns">
                    <div class="column">
                        <fieldset class="fieldset">
                            <legend>{{ optionalText }}</legend>
                            <div class="columns">
                                <div class="column">
                                    <b-field :label="quantityText" :type="{ 'is-danger': errors.has('quantity') }" :message="quantityerrorMessage">
                                        <b-input type="number" v-model="currentMedication.MetricAmount" :placeholder="optionalPlaceholderText" :disabled="isFromRxHistory" step="1" icon-pack="fa"
                                                 :key="currentMedication.Id + 'Quantity'" name="quantity" v-validate="'decimal|between:0,10000000'" :input="updateQuantity()"></b-input>
                                    </b-field>
                                </div>
                            </div>

                            <div class="columns">
                                <div class="column">
                                    <b-field :label="daysSupplyText" :type="{ 'is-danger': errors.has('days supply') }" :message="daysupplyerrorMessage">
                                        <b-input type="number" v-model="currentMedication.DaysSupply" :placeholder="optionalPlaceholderText" :disabled="isFromRxHistory" step="1" icon-pack="fa"
                                                 :key="'DaysSupply'" name="days supply" v-validate="'integer|between:0,1000'" :input="updateDaySupply()"></b-input>
                                    </b-field>
                                </div>
                            </div>

                            <div class="columns">
                                <div class="column">
                                    <b-field :label="copayText" :type="{ 'is-danger': errors.has('copay') }" :message="copayerrorMessage">
                                        <b-input type="number" v-model="currentMedication.Copay" :placeholder="optionalPlaceholderText" :disabled="isFromRxHistory" step=".01" icon-pack="fa"
                                                 :key="'Copay'" name="copay" v-validate="'decimal:2|between:0.00,10000000.00'" :input="updateCopay()"></b-input>
                                    </b-field>
                                </div>
                            </div>

                            <div class="columns">
                                <div class="column">
                                    <b-dropdown aria-role="list" @change="labelActionChanged">
                                        <button id="label-button" class="button" type="button" slot="trigger" v-model="labelAction">
                                            <span>{{ labelAction.text }}</span>
                                            <b-icon icon="caret-down" pack="fa"></b-icon>
                                        </button>

                                        <b-dropdown-item aria-role="listitem" :value="labelActions.toggle">{{applyLabelsText}}</b-dropdown-item>
                                        <b-dropdown-item aria-role="listitem" :value="labelActions.customize">{{customizeLabelsText}}</b-dropdown-item>
                                    </b-dropdown>

                                    <button class="button is-info" type="button" @click="labelActionClick">{{ goText }}</button>
                                </div>
                            </div>
                        </fieldset>
                    </div>
                </div>

                <div class="columns">
                    <div class="column">
                        <b-input type="textarea" v-model.lazy="currentMedication.Notes" rows="5" :placeholder="notePlaceholderText"></b-input>
                    </div>
                </div>

            </form>
        </div>

        <!-- action buttons -->
        <div class="actions" v-bind:class="buttonsClass">
            <div class="columns">
                <div class="column" v-if="mode === 'update'">
                    <button class="button is-fullwidth is-danger" v-on:click="deleteMedicationClick">{{ removeText }}</button>
                </div>
            </div>
            <div class="columns">
                <div class="column">
                    <button class="button is-fullwidth is-info" v-on:click="saveMedicationClick">{{ mode === "update" ? updateText : addToCabinetText }}</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script type="text/javascript">
    import CabinetResource from "../models/CabinetResource.js";
    import ApolloFilters from "../models/ApolloFilters.js";
    import Modal from "./Modal.vue";
    import UserLabel from "../models/UserLabel.js"
    import _ from "underscore";
    import axios from "axios";
    import Default from "../models/Default.js";

    export default {
        components: {
            Modal: Modal
        },
        props: [
            "medication",
            "mode"
        ],
        methods: {
            loadDefaults: function () {
                var me = this;

                Default.GetForView("cabinet").then(function (res) {
                    me.defaults = res.data;
                    me.loadText();
                });
            },
            getDefault: function (nameKey) {
                return Default.Find(nameKey, this.defaults);
            },
            loadText: function () {
                this.monthText = this.getDefault("months");
                this.monthText = this.monthText.split(",");
                this.enterSearchPlaceholderText = this.getDefault("enter_search_placeholder");
                this.searchPlaceholderText = this.getDefault("search_placeholder");
                this.searchButtonText = this.getDefault("search");
                this.clearButtonText = this.getDefault("clear");
                this.noResultsText = this.getDefault("no_results");
                this.proTipText = this.getDefault("pro_tip");
                this.proTipMessageText = this.getDefault("pro_tip_message");
                this.labelActions.toggle.text = this.getDefault("apply_labels");
                this.labelActions.customize.text = this.getDefault("customize_labels");
                this.notePlaceholderText = this.getDefault("note_placeholder");
                this.quantityText = this.getDefault("quantity_label");
                this.daysSupplyText = this.getDefault("days_supply_label");
                this.copayText = this.getDefault("copay_label");
                this.optionalText = this.getDefault("optional");
                this.optionalPlaceholderText = this.getDefault("optional_placeholder");
                this.fillDateText = this.getDefault("fill_date");
                this.updateText = this.getDefault("update");
                this.addToCabinetText = this.getDefault("add_to_cabinet");
                this.removeText = this.getDefault("remove");
                this.goText = this.getDefault("go");
                this.customizeLabelsText = this.getDefault("customize_labels");
                this.applyLabelsText = this.getDefault("apply_labels");
                this.optionalText = this.getDefault("optional");
                this.requiredText = this.getDefault("required");
                this.editLabelsText = this.getDefault("edit_labels");
                this.labelPlaceholderText = this.getDefault("label_placeholder");
                this.saveText = this.getDefault("save");
                this.toggleLabelsText = this.getDefault("toggle_labels");
                this.setLabelsText = this.getDefault("set_labels");
                this.okText = this.getDefault("ok");
                console.log(this.labelPlaceholderText);
            },
            saveMedicationClick: function () {
                var me = this,
                    verb = this.mode === "update" ? "put" : "post";
                this.$validator.validateAll()
                    .then(function (result) {
                        if (result) {
                            // update fill date
                            me.currentMedication.FillData = ApolloFilters.dateYYYYMMDD(me.currentMedication.FillDate);
                            // set labels
                            me.updateMedicationLabels();
                            // save
                            CabinetResource[verb](me.currentMedication)
                                .then(function (response) {
                                    me.$emit("save", me.currentMedication);
                                    me.$buefy.toast.open({
                                        message: me.getDefault("save_success"),
                                        type: "is-info"
                                    });
                                })
                                .catch(function (error) {
                                    console.log(error);
                                    me.$buefy.toast.open({
                                        message: me.getDefault("save_failed"),
                                        type: "is-danger"
                                    });
                                });

                            axios.post(Apollo.resolveApiUrl("~/log/info"), {
                                "location": "Cabinet",
                                "message": "Add to Medication button clicked"
                            });
                        }
                        else {
                            me.$buefy.toast.open({
                                message: me.getDefault("save_invalid"),
                                type: "is-danger"
                            });
                        }
                    });
            },
            updateQuantity: function () {
                if (this.currentMedication.MetricAmount > 10000000 || this.currentMedication.MetricAmount < 0) {
                    this.quantityerrorMessage = this.getDefault("quantity_error"); 
                } else {
                    this.quantityerrorMessage = null;
                }
            },
            updateDaySupply: function () {
                if (this.currentMedication.DaysSupply > 1000 || this.currentMedication.DaysSupply < 0) {
                    this.daysupplyerrorMessage = this.getDefault("day_supply_error"); 
                } else {
                    this.daysupplyerrorMessage = null;
                }
            },
            updateCopay: function () {
                if (this.currentMedication.Copay > 10000000 || this.currentMedication.Copay < 0) {
                    this.copayerrorMessage = this.getDefault("copay_error"); 
                } else {
                    this.copayerrorMessage = null;
                }
            },
            deleteMedication: function () {
                var me = this;
                // delete the medication & close
                CabinetResource.delete(this.currentMedication)
                    .then(function (response) {
                        // fire medication removed event
                        me.$emit("remove", me.currentMedication);

                        me.$buefy.toast.open({
                            message: me.getDefault("remove_success"),
                            type: "is-info"
                        });
                    })
                    .catch(function (error) {
                        console.log(error);
                        me.$buefy.toast.open({
                            message: me.getDefault("remove_failed"),
                            type: "is-danger"
                        });
                    });
            },
            deleteMedicationClick: function () {
                this.$buefy.dialog.alert({
                    title: this.getDefault("confirm"),
                    message: this.getDefault("remove_drug"),
                    confirmText: this.getDefault("remove"),
                    cancelText: this.getDefault("cancel"),
                    canCancel: true,
                    hasIcon: true,
                    iconPack: "fa",
                    type: "is-danger",
                    onConfirm: this.deleteMedication
                });
            },
            loadLabels: function (resetChecked) {
                var me = this;
                UserLabel.get()
                    .then(function (response) {
                        me.labels = response.data;
                        if (resetChecked) {
                            me.checkedLabels = [];
                            me.currentMedication.Labels.forEach(function (label) {
                                me.checkedLabels.push(label.LabelId);
                            });
                            // set applied label status
                        }
                    });
            },
            updateMedicationLabels: function () {
                var me = this;
                me.currentMedication.Labels = [];
                me.checkedLabels.forEach(function (id) {
                    var userLabel = _.findWhere(me.labels, { LabelId: id });
                    me.currentMedication.Labels.push(userLabel);
                });
            },
            toggleLabelForm: function () {
                this.isLabelFormOpen = !this.isLabelFormOpen;
            },
            labelActionClick: function () {
                // the "go" label action button
                if (this.labelAction === this.labelActions.toggle) {
                    this.isLabelFormOpen = !this.isLabelFormOpen;
                }
                else if (this.labelAction === this.labelActions.customize) {
                    this.tempLabels = JSON.parse(JSON.stringify(this.labels));
                    this.isLabelEditFormOpen = !this.isLabelEditFormOpen;
                }
            },
            labelTextChange: function (label) {
                // track labels that have been changed
                label._dirty = true;
            },
            labelEditSaveClick: function () {
                // save any updated labels
                var me = this,
                    saves = [];
                for (let i = 0; i < this.tempLabels.length; i++) {
                    let label = this.tempLabels[i];
                    if (label._dirty) {
                        // check if new or update and apply
                        saves.push(UserLabel[label.UserId ? "put" : "post"](label));
                    }
                }
                // save in parallel
                axios.all(saves)
                    .then(axios.spread(function () {
                        me.$emit("labels-save");
                        me.isLabelEditFormOpen = false;
                        me.loadLabels(false);
                        me.$buefy.toast.open({
                            message: me.getDefault("label_success"),
                            type: "is-info"
                        });
                    }))
                    .catch(function (error) {
                        me.$emit("error", error);

                        me.$buefy.toast.open({
                            message: me.getDefault("label_failed"),
                            type: "is-danger"
                        });
                    });
            },
            validate: function () {
                this.$validator.validateAll()
                    .then(function (result) {
                        if (!result) {
                            console.log("invalid form");
                        }
                    });
            },
            labelActionChanged: function (action) {
                this.labelAction = action;
            },
            dateFormatter: function (d) {
                return ApolloFilters.datePicker(d);
            }
        },
        data: function () {
            var me = this,
                data = {
                    // clone object so that the view doesn't update until we save
                    currentMedication: JSON.parse(JSON.stringify(me.medication)),
                    labels: [],
                    // will be used for the edit form
                    tempLabels: [],
                    checkedLabels: [],
                    isLabelFormOpen: false,
                    isLabelEditFormOpen: false,
                    enterSearchPlaceholderText: "",
                    searchPlaceholderText: "",
                    searchButtonText: "",
                    clearButtonText: "",
                    noResultsText: "",
                    proTipText: "",
                    proTipMessageText: "",
                    notePlaceholderText: "",
                    quantityText: "",
                    daysSupplyText: "",
                    copayText: "",
                    optionalText: "",
                    optionalPlaceholderText: "",
                    fillDateText: "",
                    updateText: "",
                    addToCabinetText: "",
                    removeText: "",
                    goText: "",
                    customizeLabelsText: "",
                    applyLabelsText: "",
                    optionalText: "",
                    requiredText: "",
                    editLabelsText: "",
                    labelPlaceholderText: "",
                    saveText: "",
                    toggleLabelsText: "",
                    setLabelsText: "",
                    monthText: [],
                    okText: "",
                    labelActions: {
                        toggle: {
                            text: ""
                        },
                        customize: {
                            text: ""
                        }
                    }
                };
            data.labelAction = data.labelActions.toggle;
            // convert fill date to a date
            data.currentMedication.FillDate = new Date(data.currentMedication.FillDate);
            data.currentMedication.FillDate.setMinutes(data.currentMedication.FillDate.getTimezoneOffset());
            return data;
        },
        filters: ApolloFilters,
        created: function () {
            this.loadDefaults();
            this.loadLabels(true);
        },
        computed: {
            buttonsClass: function () {
                return {
                    "buttons-1": this.mode === "create",
                    "buttons-2": this.mode === "update"
                };
            },
            isFromRxHistory: function () {
                return this.currentMedication && this.currentMedication.Source === 2;
            }
        }
    };
</script>



// WEBPACK FOOTER //
// CabinetForm.vue?7d18d4f9